import React from 'react';
import { motion } from 'framer-motion';
import { formatTime, getStatusColor, getStatusLabel } from '../utils/formatters';

interface Course {
  _id: string;
  name: string;
  description: string;
  startDate: string;
  classDays: string[];
  timeSlots: any[];
  maxStudents: number;
  enrolledCount: number;
  level: any;
  format: string;
  status: string;
  price: number;
}

interface CourseTableProps {
  courses: Course[];
  visibleCourses: number;
  newlyLoadedCourses: string[];
  isLoading: boolean;
}

const CourseTable: React.FC<CourseTableProps> = ({
  courses,
  visibleCourses,
  newlyLoadedCourses,
  isLoading,
}) => {
  const displayedCourses = courses.slice(0, visibleCourses);

  return (
    <motion.div
      className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 mt-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 border-b border-gray-200 dark:border-gray-700">
            <th className="px-6 py-4 text-left font-semibold text-gray-800 dark:text-gray-200">
              Khóa học
            </th>
            <th className="px-6 py-4 text-left font-semibold text-gray-800 dark:text-gray-200">
              Trình độ
            </th>
            <th className="px-6 py-4 text-left font-semibold text-gray-800 dark:text-gray-200">
              Khai giảng
            </th>
            <th className="px-6 py-4 text-left font-semibold text-gray-800 dark:text-gray-200">
              Buổi/Ca
            </th>
            <th className="px-6 py-4 text-left font-semibold text-gray-800 dark:text-gray-200">
              Sĩ số
            </th>
            <th className="px-6 py-4 text-left font-semibold text-gray-800 dark:text-gray-200">
              Hình thức
            </th>
            <th className="px-6 py-4 text-left font-semibold text-gray-800 dark:text-gray-200">
              Trạng thái
            </th>
            <th className="px-6 py-4 text-left font-semibold text-gray-800 dark:text-gray-200">
              Giá
            </th>
          </tr>
        </thead>
        <tbody>
          {displayedCourses.map((course, index) => {
            const isNewlyLoaded = newlyLoadedCourses.includes(course._id);
            return (
              <motion.tr
                key={course._id}
                initial={isNewlyLoaded ? { opacity: 0, y: 10 } : { opacity: 1 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`border-b border-gray-200 dark:border-gray-700 hover:bg-teal-50 dark:hover:bg-gray-800 transition-colors ${
                  isLoading ? 'opacity-50' : ''
                }`}
              >
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {course.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {course.description}
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                  {course.level?.name || 'N/A'}
                </td>
                <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                  {course.startDate}
                </td>
                <td className="px-6 py-4 text-gray-700 dark:text-gray-300 text-xs">
                  <div>{course.classDays?.join(', ') || 'N/A'}</div>
                  <div className="text-gray-500 dark:text-gray-500">
                    {course.timeSlots?.[0]
                      ? `${formatTime(course.timeSlots[0].startTime)}`
                      : 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                  <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                    {course.enrolledCount}/{course.maxStudents}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                  <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                    {course.format === 'online' ? 'Trực tuyến' : 'Tại lớp'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`text-xs px-2 py-1 rounded font-medium ${getStatusColor(
                      course.status
                    )}`}
                  >
                    {getStatusLabel(course.status)}
                  </span>
                </td>
                <td className="px-6 py-4 font-semibold text-[#017e6e] dark:text-emerald-400">
                  {course.price?.toLocaleString('vi-VN')}đ
                </td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </motion.div>
  );
};

export default CourseTable;
